import React, { useState } from 'react';
import clsx from 'clsx';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useTranslation } from 'next-i18next';
import { useMutation } from '@tanstack/react-query';
import { submitReturnForm } from '@services/my-orders-sales-service';
import useTimelineQuery from '@hooks/orders-sales/use-timeline-query';
import { ReturnFormParam } from '@interfaces/api/payloads/timeline-action';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import Icon from '@components/atoms/icon/icon';
import styles from '../popin.module.scss';
import { CancelButtonView, getErrorString, OkButtonView, OkView, PopinView } from './common';

type ReturnFormMutationParam = {
  productId: string;
  param: ReturnFormParam;
};
const ReturnFormView: React.FC<{
  closePopin: () => void;
  product: Product;
  order: Order;
  returnReasons: Record<string, string>;
}> = ({ closePopin, product, order, returnReasons }) => {
  const { t } = useTranslation('orders-sales');
  const { t: tA11y } = useTranslation('a11y');
  const reasonKeys = Object.keys(returnReasons);
  const { refetchTimeline } = useTimelineQuery(product.productId, order ? order.orderId : null, false);

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');
  const analyticData = { label: 'buyer', product_id: product.productId };

  const [formState, updateFormState] = useState<{
    reason: string;
    detail: string;
    formError: boolean;
  }>({ reason: reasonKeys[0], detail: '', formError: false });
  const { reason, detail, formError } = formState;

  const { isSuccess, isLoading, isError, data, mutate } = useMutation({
    mutationFn: ({ productId, param }: ReturnFormMutationParam) => {
      return submitReturnForm(productId, param);
    },
  });
  const hasError = isError || (data != null && data.kind !== 'Success');
  const errorMessage = data ? getErrorString(t, data) : null;
  const hasSuccess = isSuccess && data != null && data.kind === 'Success';

  if (hasSuccess) {
    return (
      <PopinView
        closePopin={() => {
          refetchTimeline();
          closePopin();
        }}
        title={t('MY_ACCOUNT.ORDERS_SALES.POPUP.RETURN_FORM.TITLE')}
      >
        <OkView message={t('MY_ACCOUNT.ORDERS_SALES.COMPLETED')} />
      </PopinView>
    );
  }

  const errorView = formError ? (
    <div className={styles.ordersSales__popin__errorText}>
      {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RETURN_FORM.ERROR_REASON')}
    </div>
  ) : hasError ? (
    <div className={styles.ordersSales__popin__errorText}>{errorMessage || t('MY_ACCOUNT.ORDERS_SALES.ERROR.API')}</div>
  ) : (
    <></>
  );

  return (
    <PopinView
      closePopin={closePopin}
      title={t('MY_ACCOUNT.ORDERS_SALES.POPUP.RETURN_FORM.TITLE')}
    >
      <div className={clsx('vc-modal__content', styles.ordersSales__popin__content)}>
        <div className={styles.ordersSales__popin__returnForm__fields}>
          <div className={styles.ordersSales__popin__returnForm__label}>
            {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RETURN_FORM.FIELD_REASON')}
          </div>
          <div className={styles.ordersSales__popin__select}>
            <select
              value={reason}
              onChange={(e) => {
                updateFormState({ ...formState, reason: String(e.target.value), formError: false });
              }}
            >
              {reasonKeys.map((key) => (
                <option
                  value={key}
                  key={key}
                >
                  {returnReasons[key]}
                </option>
              ))}
            </select>
            <Icon
              name="arrow-head-down"
              className={styles.ordersSales__popin__select__icon}
              ariaLabel={tA11y('A11Y.ICON.DROPDOWN_DOWN')}
            />
          </div>
          <div />
          <div />
          <div className={styles.ordersSales__popin__returnForm__label}>
            {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RETURN_FORM.FIELD_DETAIL')}
          </div>
          <div className={styles.ordersSales__popin__returnForm__textarea}>
            <textarea
              value={detail}
              rows={5}
              onChange={(e) => {
                updateFormState({ ...formState, detail: String(e.target.value), formError: false });
              }}
            />
          </div>
          <div className={styles.ordersSales__popin__returnForm__hint}>
            {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RETURN_FORM.HINT')}
          </div>
        </div>
        {errorView}
        <div className={styles.ordersSales__popin__buttons}>
          <CancelButtonView
            isSubmitting={isLoading}
            onClick={() => {
              sendAnalyticEvent('timeline_b2c_return_cancel', analyticData);
              closePopin();
            }}
          >
            {t('MY_ACCOUNT.ORDERS_SALES.CANCEL')}
          </CancelButtonView>
          <OkButtonView
            isSubmitting={isLoading}
            onClick={() => {
              if (reason != '16' && detail.trim() === '') {
                updateFormState({ ...formState, formError: true });
              } else {
                sendAnalyticEvent('timeline_b2c_return_submit', analyticData);
                mutate({
                  productId: product.productId,
                  param: { orderId: Number(order.orderId), returnReason: Number(reason), detailReasons: detail },
                });
              }
            }}
          >
            {t('MY_ACCOUNT.ORDERS_SALES.SUBMIT')}
          </OkButtonView>
        </div>
      </div>
    </PopinView>
  );
};

export default ReturnFormView;
